/**
 * /transport/request — public buyer transport request form
 * -----------------------------------------------------------
 * Three-step form that captures a buyer's transport need. No auth
 * required so first-time visitors can submit without signing up;
 * we capture their email to follow up if they bounce before payment.
 *
 * Steps
 *   1. Route (pickup + dropoff city/state + flexibility window)
 *   2. Dog details (count, breed size, optional litter reference)
 *   3. Contact info (email, name, phone, notes)
 *
 * On submit we POST to /api/transport/request and route to
 * /transport/orders/{id} where the buyer can come back via email link.
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, MapPinIcon as MapPin, HeartIcon as Heart, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import DateOfBirthInput from "@/components/DateOfBirthInput";

const SIZES = [
  { value: "toy", label: "Toy (≤ 10 lb)" },
  { value: "small", label: "Small (10-25 lb)" },
  { value: "medium", label: "Medium (25-50 lb)" },
  { value: "large", label: "Large (50-90 lb)" },
  { value: "giant", label: "Giant (90+ lb)" },
];

const BLANK = {
  pickup_city: "",
  pickup_state: "",
  dropoff_city: "",
  dropoff_state: "",
  requested_pickup_date: "",
  flexibility_days: 3,
  dog_count: 1,
  breed_size: "",
  buyer_name: "",
  buyer_email: "",
  buyer_phone: "",
  notes: "",
  venue_acknowledged: false,
  ack_refund_policy: false,
};

export default function TransportRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);

  // Each step has its own minimal client-side validation so a buyer
  // can't bypass required fields by mashing "Next" — gives them
  // immediate feedback and shrinks the validation error surface from
  // the API.
  const canAdvanceFromStep1 =
    form.pickup_city.trim() && form.pickup_state.length === 2 &&
    form.dropoff_city.trim() && form.dropoff_state.length === 2;
  const canAdvanceFromStep2 = form.dog_count > 0;
  const canSubmit = form.buyer_email.trim().includes("@") && form.venue_acknowledged && form.ack_refund_policy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // States are 2-letter codes; normalize so we don't 422 on case.
      const payload = {
        ...form,
        pickup_state: form.pickup_state.toUpperCase(),
        dropoff_state: form.dropoff_state.toUpperCase(),
        flexibility_days: Number(form.flexibility_days) || 0,
        dog_count: Number(form.dog_count) || 1,
        breed_size: form.breed_size || null,
        requested_pickup_date: form.requested_pickup_date || null,
        buyer_name: form.buyer_name || null,
        buyer_phone: form.buyer_phone || null,
        notes: form.notes || null,
        venue_acknowledged: !!form.venue_acknowledged,
        ack_refund_policy: !!form.ack_refund_policy,
      };
      const res = await api.post("/transport/request", payload);
      toast.success("Request submitted", {
        description: "We'll match you with a verified transporter within 24 hours.",
      });
      navigate(`/transport/orders/${res.data.order.id}?status=submitted`);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="transport-request-page">
        <Link to="/" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← Back to Armstrong K9 Registry
        </Link>
        <div className="flex items-center gap-3 mt-3 mb-1">
          <Truck size={32} weight="duotone" className="text-ink" />
          <h1 className="font-spectral text-4xl tracking-tight">
            Request a transporter
          </h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] leading-relaxed mb-6 max-w-2xl">
          Tell us about your trip. We'll match you with a USDA-licensed,
          insured hauler from our verified network and send a confirmed
          quote within 24 hours.{" "}
          <strong className="text-ink">Payment is escrowed</strong> until
          the dog is delivered safely.
        </p>

        {/* Trust strip */}
        <div className="flex flex-wrap gap-3 mb-4 text-[12px] text-[#6B6B6B]">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <ShieldCheck size={14} weight="duotone" className="text-emerald-600" />
            USDA-licensed only
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <Heart size={14} weight="duotone" className="text-crimson" />
            $300K liability minimum
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <Truck size={14} weight="duotone" className="text-ink" />
            48hr escrow protection
          </span>
        </div>

        {/* Cross-sell: every US transport buyer who's importing from
            abroad is also a potential intl-courier lead. One CTA here
            captures the half of the audience whose trip starts in a
            foreign country. Free conversion — zero acquisition cost. */}
        <div className="rounded-xl border border-[#E8E0CC] bg-cream p-3 mb-6 flex items-start gap-3" data-testid="intl-courier-crosssell">
          <span className="inline-flex h-9 w-9 rounded-lg bg-gold-gradient items-center justify-center flex-shrink-0">
            <Truck size={16} weight="bold" className="text-ink" />
          </span>
          <div className="flex-1 text-[13px]">
            <div className="font-semibold text-ink">Importing from abroad?</div>
            <div className="text-[#6B6B6B] mb-1.5">USDA-licensed haulers run domestic routes only. For pickup in a foreign country, request an international courier directly.</div>
            <Link to="/transport/international" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#C1272D] hover:text-ink" data-testid="intl-courier-crosssell-link">
              See verified intl couriers on your route →
            </Link>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold ${
                  step >= n
                    ? "bg-ink text-white"
                    : "bg-[#F5F5F5] text-[#9A9A9A]"
                }`}
                data-testid={`step-indicator-${n}`}
              >
                {n}
              </div>
              {n < 3 && (
                <div className={`h-0.5 flex-1 ${step > n ? "bg-ink" : "bg-[#EAEAEA]"}`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="transport-request-form">
          {step === 1 && (
            <section className="space-y-4">
              <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
                <MapPin size={20} weight="duotone" className="text-ink" />
                Route
              </h2>
              <div className="grid sm:grid-cols-[1fr,auto] gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Pickup city *</Label>
                  <Input
                    value={form.pickup_city}
                    onChange={(e) => setForm({ ...form, pickup_city: e.target.value })}
                    placeholder="Memphis"
                    required
                    className="mt-1"
                    data-testid="pickup-city"
                  />
                </div>
                <div className="w-24">
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">State *</Label>
                  <Input
                    value={form.pickup_state}
                    onChange={(e) => setForm({ ...form, pickup_state: e.target.value.slice(0, 2) })}
                    placeholder="TN"
                    maxLength={2}
                    required
                    className="mt-1 uppercase"
                    data-testid="pickup-state"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-[1fr,auto] gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Dropoff city *</Label>
                  <Input
                    value={form.dropoff_city}
                    onChange={(e) => setForm({ ...form, dropoff_city: e.target.value })}
                    placeholder="Atlanta"
                    required
                    className="mt-1"
                    data-testid="dropoff-city"
                  />
                </div>
                <div className="w-24">
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">State *</Label>
                  <Input
                    value={form.dropoff_state}
                    onChange={(e) => setForm({ ...form, dropoff_state: e.target.value.slice(0, 2) })}
                    placeholder="GA"
                    maxLength={2}
                    required
                    className="mt-1 uppercase"
                    data-testid="dropoff-state"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Ideal pickup date</Label>
                  <DateOfBirthInput
                    value={form.requested_pickup_date}
                    onChange={(v) => setForm({ ...form, requested_pickup_date: v })}
                    min={new Date().toISOString().slice(0, 10)}
                    testId="pickup-date"
                    autoComplete="off"
                    ariaLabel="Ideal pickup date"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Flexibility (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={form.flexibility_days}
                    onChange={(e) => setForm({ ...form, flexibility_days: e.target.value })}
                    className="mt-1"
                    data-testid="flexibility-days"
                  />
                </div>
              </div>
              <Button
                type="button"
                disabled={!canAdvanceFromStep1}
                onClick={() => setStep(2)}
                className="rounded-full bg-ink hover:bg-crimson text-white px-6"
                data-testid="step1-next"
              >
                Continue <ArrowRight size={14} className="ml-2" />
              </Button>
            </section>
          )}

          {step === 2 && (
            <section className="space-y-4">
              <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
                <Heart size={20} weight="duotone" className="text-crimson" />
                Dog details
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">How many dogs? *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={form.dog_count}
                    onChange={(e) => setForm({ ...form, dog_count: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="dog-count"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Approximate size</Label>
                  <select
                    value={form.breed_size}
                    onChange={(e) => setForm({ ...form, breed_size: e.target.value })}
                    className="mt-1 w-full border border-[#E5E5E5] rounded-md px-3 h-10 text-[13px] bg-white"
                    data-testid="breed-size"
                  >
                    <option value="">Select size…</option>
                    {SIZES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="rounded-full"
                  data-testid="step2-back"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  disabled={!canAdvanceFromStep2}
                  onClick={() => setStep(3)}
                  className="rounded-full bg-ink hover:bg-crimson text-white px-6"
                  data-testid="step2-next"
                >
                  Continue <ArrowRight size={14} className="ml-2" />
                </Button>
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="space-y-4">
              <h2 className="font-spectral text-2xl tracking-tight">Your contact info</h2>
              <p className="text-[12px] text-[#6B6B6B]">
                We'll send your quote to this email within 24 hours.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Your name</Label>
                  <Input
                    value={form.buyer_name}
                    onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                    className="mt-1"
                    data-testid="buyer-name"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Email *</Label>
                  <Input
                    type="email"
                    value={form.buyer_email}
                    onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="buyer-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Phone</Label>
                <Input
                  value={form.buyer_phone}
                  onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })}
                  placeholder="+1 ..."
                  className="mt-1"
                  data-testid="buyer-phone"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                  Anything else we should know?
                </Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={4}
                  className="mt-1"
                  placeholder="Crate needs, breeder name, time constraints..."
                  data-testid="notes"
                />
              </div>
              <div
                className="rounded-xl border border-[#E8D9A6] bg-[#FBF7EC] p-4 text-[12.5px] leading-snug text-[#3B0E07]"
                data-testid="transport-venue-ack-block"
              >
                <p className="font-bold uppercase tracking-[0.18em] text-[10px] text-[#7A1F12] mb-2">
                  Venue role &amp; at-own-risk
                </p>
                <p className="mb-3">
                  Armstrong Transport introduces you to independent, licensed
                  haulers. <strong>Armstrong is not the carrier</strong>, is
                  not a party to the transport contract, and is not liable for
                  the acts, omissions, or outcomes of any transporter. You
                  book transport <strong>at your own risk</strong>.{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                    Read the full Terms
                  </a>
                  .
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.venue_acknowledged}
                    onChange={(e) =>
                      setForm({ ...form, venue_acknowledged: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 accent-[#7A1F12]"
                    data-testid="transport-venue-ack-checkbox"
                  />
                  <span>
                    I acknowledge that Armstrong K9 Registry is a venue (not
                    the transporter, not a party to my booking) and I am
                    requesting transport at my own risk.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.ack_refund_policy}
                    onChange={(e) =>
                      setForm({ ...form, ack_refund_policy: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 accent-[#7A1F12]"
                    data-testid="transport-refund-policy-ack-checkbox"
                  />
                  <span>
                    I&apos;ve read the{" "}
                    <a
                      href="/refund-policy#9-3-disputes"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      Refund Policy §9.1 &amp; §9.3
                    </a>{" "}
                    and understand:{" "}
                    <strong>clean cancellations</strong> follow the §9.1 tier
                    ladder (100% refund &gt;72h, 75% refund 24–72h, 50% refund
                    &lt;24h, none after pickup); <strong>filed disputes</strong>{" "}
                    follow §9.3 (mutual written consent, or — failing that —
                    a 30-day tiered default: 100% refund if filed &gt;72h pre-pickup,
                    50/50 split if 24–72h, full release to the transporter if
                    &lt;24h or trip already started). Armstrong K9 Registry
                    administers the timer and the payment rails — never the merits.
                    Stripe chargebacks remain available as a parallel recourse.
                  </span>
                </label>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="rounded-full"
                  data-testid="step3-back"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="rounded-full bg-crimson hover:bg-ink text-white px-6"
                  data-testid="submit-request"
                >
                  {submitting ? "Submitting…" : "Request transporter"}
                </Button>
              </div>
            </section>
          )}
        </form>
      </div>
    </AppShell>
  );
}
